import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Send, Search, RefreshCw, MessageCircle, UserPlus, Clock, Settings as SettingsIcon,
  Phone, User as UserIcon, CheckCheck, Check, AlertCircle, FileText, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.replace(' ', 'T'));
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  } catch { return iso.slice(0, 16); }
};

const StatusIcon = ({ status }) => {
  if (status === 'read') return <CheckCheck className="w-3.5 h-3.5 text-sky-500" title="Read" />;
  if (status === 'delivered') return <CheckCheck className="w-3.5 h-3.5 text-gray-400" title="Delivered" />;
  if (status === 'sent') return <Check className="w-3.5 h-3.5 text-gray-400" title="Sent" />;
  if (status === 'failed') return <AlertCircle className="w-3.5 h-3.5 text-red-500" title="Failed" />;
  return null;
};

const CRMPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // phone
  const [thread, setThread] = useState({ messages: [], conversation: null });
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [search, setSearch] = useState('');
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateSelected, setTemplateSelected] = useState(null);
  const [templateVars, setTemplateVars] = useState([]);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [saveContactOpen, setSaveContactOpen] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  // Quick replies (slash-command insert)
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrPopoverOpen, setQrPopoverOpen] = useState(false);
  const [qrFilter, setQrFilter] = useState('');
  const threadEndRef = useRef(null);

  // ---- Loaders ----
  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.get('/crm/conversations');
      setConversations(res.data.rows || []);
    } catch (e) {
      // silent — likely provider not configured yet
      setConversations([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (phone) => {
    if (!phone) return;
    setLoadingThread(true);
    try {
      const res = await api.get(`/crm/conversations/${encodeURIComponent(phone)}/messages`);
      setThread(res.data);
      await api.post(`/crm/conversations/${encodeURIComponent(phone)}/mark-read`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load messages');
    } finally {
      setLoadingThread(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/crm/config');
      setConfig(res.data);
    } catch { setConfig(null); }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get('/crm/templates', { params: { status: 'APPROVED' } });
      setTemplates(res.data.data || []);
    } catch { setTemplates([]); }
  }, []);

  const loadQuickReplies = useCallback(async () => {
    try {
      const res = await api.get('/crm/quick-replies');
      setQuickReplies(res.data.rows || []);
    } catch { setQuickReplies([]); }
  }, []);

  useEffect(() => {
    loadConfig();
    loadConversations();
    loadTemplates();
    loadQuickReplies();
  }, [loadConfig, loadConversations, loadTemplates, loadQuickReplies]);

  // Load the thread when the user picks a conversation
  useEffect(() => {
    if (selected) loadThread(selected);
  }, [selected, loadThread]);

  // Poll every 15s for new messages
  useEffect(() => {
    const t = setInterval(() => {
      loadConversations();
      if (selected) loadThread(selected);
    }, 15000);
    return () => clearInterval(t);
  }, [selected, loadConversations, loadThread]);

  useEffect(() => {
    // scroll to bottom on thread change
    setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [thread.messages?.length]);

  // ---- Derived ----
  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.trim().toLowerCase();
    return conversations.filter((c) =>
      (c.phone || '').includes(q) ||
      (c.patient_name || '').toLowerCase().includes(q) ||
      (c.last_message_preview || '').toLowerCase().includes(q)
    );
  }, [conversations, search]);

  const activeConversation = thread.conversation || (selected ? { phone: selected } : null);
  const isConfigured = !!config?.configured;
  const isMeta = config?.provider === 'meta';

  // ---- Actions ----
  const sendText = async () => {
    if (!selected || !composer.trim()) return;
    setSending(true);
    try {
      await api.post('/crm/send', { to: selected, text: composer.trim() });
      setComposer('');
      await loadThread(selected);
      loadConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const openTemplatePicker = () => {
    setTemplateSelected(null);
    setTemplateVars([]);
    setTemplatePickerOpen(true);
  };

  const sendTemplate = async () => {
    if (!selected || !templateSelected) return;
    setSendingTemplate(true);
    try {
      await api.post('/crm/send', {
        to: selected,
        template_name: templateSelected.name,
        template_language: templateSelected.language || 'en',
        template_variables: templateVars,
      });
      setTemplatePickerOpen(false);
      await loadThread(selected);
      loadConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send template');
    } finally {
      setSendingTemplate(false);
    }
  };

  const saveAsPatient = async () => {
    if (!selected || !newContactName.trim()) return;
    setSavingContact(true);
    try {
      await api.post('/patients', {
        name: newContactName.trim(),
        primary_phone: selected,
      });
      toast.success('Patient created');
      setSaveContactOpen(false);
      setNewContactName('');
      await loadConversations();
      await loadThread(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create patient');
    } finally {
      setSavingContact(false);
    }
  };

  const bodyVariableCount = useMemo(() => {
    if (!templateSelected) return 0;
    const body = (templateSelected.components || []).find((c) => c.type === 'BODY');
    if (!body?.text) return 0;
    const matches = body.text.match(/\{\{\d+\}\}/g) || [];
    return matches.length;
  }, [templateSelected]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex flex-col h-[calc(100vh-0px)] md:h-screen bg-gray-50">
        {/* Header */}
        <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <h1 className="text-lg md:text-xl font-bold text-gray-900">CRM Inbox</h1>
            {config && (
              <Badge className={`ml-2 ${isMeta ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}
                data-testid="crm-provider-badge">
                {isMeta ? 'Meta' : 'Interakt'}
              </Badge>
            )}
            {!isConfigured && (
              <Badge className="bg-amber-100 text-amber-700 ml-1">Not configured</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { loadConversations(); if (selected) loadThread(selected); }}
              data-testid="crm-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            {user?.role === 'Admin' && (
              <Button variant="outline" size="sm" onClick={() => navigate('/crm/settings')} data-testid="crm-settings-btn">
                <SettingsIcon className="w-4 h-4 mr-1" /> Setup
              </Button>
            )}
          </div>
        </div>

        {!isConfigured && (
          <div className="mx-3 md:mx-6 mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 flex items-start gap-2" data-testid="crm-not-configured-banner">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              Meta WhatsApp is not fully configured yet. Set it up under <button onClick={() => navigate('/crm/settings')} className="underline font-medium">Meta Setup</button> to start sending &amp; receiving.
            </div>
          </div>
        )}

        {/* Split view */}
        <div className="flex-1 flex overflow-hidden">
          {/* Conversation list */}
          <div className={`${selected ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r bg-white`}>
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search phone / name / message"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="crm-search-input"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="crm-conversation-list">
              {loadingList && (
                <div className="p-6 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
                </div>
              )}
              {!loadingList && filteredConversations.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-400" data-testid="crm-empty-list">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  {conversations.length === 0
                    ? 'No conversations yet. Incoming messages will appear here.'
                    : 'No conversations match your search.'}
                </div>
              )}
              {filteredConversations.map((c) => (
                <button
                  key={c.phone}
                  onClick={() => setSelected(c.phone)}
                  className={`w-full text-left px-3 py-2 border-b hover:bg-emerald-50 transition ${selected === c.phone ? 'bg-emerald-50' : ''}`}
                  data-testid={`crm-conv-${c.phone}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 truncate">
                          {c.patient_name || (
                            <span className="text-orange-600 italic">Unknown</span>
                          )}
                        </p>
                        {c.within_24h && (
                          <Badge className="bg-green-100 text-green-700 text-[9px] py-0 px-1">24h</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-mono">{c.phone}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {c.last_direction === 'outbound' && <span className="text-gray-400">You: </span>}
                        {c.last_message_preview || '—'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[10px] text-gray-400">{fmtTime(c.last_message_at)}</span>
                      {c.unread_count > 0 && (
                        <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white text-[10px] h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full" data-testid={`crm-unread-${c.phone}`}>
                          {c.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Thread pane */}
          <div className={`${selected ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0 bg-gray-50`}>
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  Pick a conversation to view messages
                </div>
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b bg-white">
                  <div className="flex items-center gap-2 min-w-0">
                    <button className="md:hidden p-1" onClick={() => setSelected(null)}>←</button>
                    <div className="p-1.5 bg-emerald-100 rounded-full">
                      <UserIcon className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate" data-testid="crm-thread-name">
                        {activeConversation?.patient_name || <span className="text-orange-600 italic">Unknown contact</span>}
                      </p>
                      <p className="text-xs text-gray-500 font-mono flex items-center gap-1">
                        <Phone className="w-3 h-3" /> +{selected}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!activeConversation?.patient_id && (
                      <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => setSaveContactOpen(true)} data-testid="crm-save-contact-btn">
                        <UserPlus className="w-4 h-4 mr-1" /> Save
                      </Button>
                    )}
                  </div>
                </div>

                {/* 24h window banner */}
                {!thread.conversation?.within_24h && (
                  <div className="mx-3 mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-800 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    24-hour reply window closed — you can only send an approved template until they message again.
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-3 md:px-5 py-4" data-testid="crm-thread-scroll">
                  {loadingThread && (
                    <div className="text-center text-sm text-gray-400 py-6"><RefreshCw className="w-4 h-4 animate-spin inline mr-1" /> Loading messages…</div>
                  )}
                  {!loadingThread && thread.messages.length === 0 && (
                    <div className="text-center text-sm text-gray-400 py-10">No messages yet.</div>
                  )}
                  <div className="space-y-1.5 max-w-3xl mx-auto">
                    {thread.messages.map((m) => {
                      const outbound = m.direction === 'outbound';
                      return (
                        <div key={m.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`} data-testid={`crm-msg-${m.id}`}>
                          <div className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm ${outbound ? 'bg-emerald-500 text-white rounded-br-sm' : 'bg-white text-gray-900 rounded-bl-sm border border-gray-200'}`}>
                            {m.type === 'template' && (
                              <p className={`text-[10px] uppercase tracking-wide mb-1 ${outbound ? 'text-emerald-100' : 'text-gray-400'}`}>Template · {m.template_name}</p>
                            )}
                            {m.type !== 'text' && m.type !== 'template' && m.type !== 'interactive' && m.type !== 'button' && (
                              <p className={`text-[10px] uppercase tracking-wide mb-1 ${outbound ? 'text-emerald-100' : 'text-gray-400'}`}>{m.type}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-0.5 text-[10px] ${outbound ? 'text-emerald-100' : 'text-gray-400'}`}>
                              <span>{fmtTime(m.timestamp)}</span>
                              {outbound && <StatusIcon status={m.status} />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={threadEndRef} />
                  </div>
                </div>

                {/* Composer */}
                <div className="border-t bg-white px-2 md:px-4 py-2 relative">
                  {/* Slash-command quick reply picker */}
                  {qrPopoverOpen && quickReplies.length > 0 && (
                    <div className="absolute bottom-full left-2 md:left-4 mb-1 w-72 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-testid="crm-qr-popover">
                      <div className="p-2 text-[10px] uppercase text-gray-400 border-b">Quick replies · <kbd className="font-mono bg-gray-100 px-1">↵</kbd> to insert</div>
                      {quickReplies
                        .filter((r) => !qrFilter || r.shortcut.includes(qrFilter) || (r.title || '').toLowerCase().includes(qrFilter.toLowerCase()))
                        .slice(0, 8)
                        .map((r) => (
                          <button key={r.id}
                            onClick={() => { setComposer(r.body); setQrPopoverOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-0"
                            data-testid={`crm-qr-item-${r.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <code className="text-emerald-700 text-xs font-mono">{r.shortcut}</code>
                              <span className="text-xs text-gray-500 truncate">{r.title}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{r.body}</p>
                          </button>
                        ))}
                    </div>
                  )}

                  <div className="flex items-end gap-2 max-w-3xl mx-auto">
                    <Button
                      variant="outline" size="sm"
                      onClick={openTemplatePicker}
                      data-testid="crm-open-template-picker"
                      className="flex-shrink-0"
                      title="Send WhatsApp template"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => { setQrFilter(''); setQrPopoverOpen((v) => !v); }}
                      disabled={!isConfigured || !thread.conversation?.within_24h || quickReplies.length === 0}
                      data-testid="crm-open-qr-picker"
                      className="flex-shrink-0"
                      title={quickReplies.length === 0 ? 'No quick replies yet (add some under CRM → Quick Replies)' : 'Insert quick reply'}
                    >
                      <Zap className="w-4 h-4" />
                    </Button>
                    <Textarea
                      value={composer}
                      onChange={(e) => {
                        const v = e.target.value;
                        setComposer(v);
                        // Auto-open picker when user types "/" at start
                        if (v.startsWith('/') && !v.includes(' ')) {
                          setQrFilter(v);
                          setQrPopoverOpen(true);
                        } else if (qrPopoverOpen && !v.startsWith('/')) {
                          setQrPopoverOpen(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Insert first matching quick reply on Enter when picker is open with "/"
                        if (qrPopoverOpen && e.key === 'Enter' && !e.shiftKey && composer.startsWith('/')) {
                          const match = quickReplies.find(
                            (r) => r.shortcut === composer.trim()
                          );
                          if (match) {
                            e.preventDefault();
                            setComposer(match.body);
                            setQrPopoverOpen(false);
                            return;
                          }
                        }
                        if (e.key === 'Enter' && !e.shiftKey && composer.trim()) {
                          e.preventDefault();
                          sendText();
                        }
                        if (e.key === 'Escape') setQrPopoverOpen(false);
                      }}
                      placeholder={
                        !isConfigured
                          ? 'Configure Meta setup first…'
                          : thread.conversation?.within_24h
                            ? 'Type a message…   (try / for quick replies)'
                            : 'Outside 24h window — pick a template →'
                      }
                      disabled={!isConfigured || !thread.conversation?.within_24h}
                      rows={1}
                      className="resize-none min-h-9 max-h-28"
                      data-testid="crm-composer-input"
                    />
                    <Button
                      onClick={sendText}
                      disabled={!isConfigured || !thread.conversation?.within_24h || !composer.trim() || sending}
                      className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
                      data-testid="crm-send-text-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Save Contact modal */}
      <Dialog open={saveContactOpen} onOpenChange={setSaveContactOpen}>
        <DialogContent data-testid="crm-save-contact-dialog">
          <DialogHeader>
            <DialogTitle>Save as new patient</DialogTitle>
            <DialogDescription>
              Create a patient profile for +{selected}. You can add more details later from the Patients page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700">Patient name</label>
              <Input
                autoFocus
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
                placeholder="e.g. Ramesh Kumar"
                data-testid="crm-new-contact-name"
              />
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Phone: <span className="font-mono">+{selected}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveContactOpen(false)}>Cancel</Button>
            <Button
              onClick={saveAsPatient}
              disabled={!newContactName.trim() || savingContact}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="crm-save-contact-submit"
            >
              {savingContact ? 'Saving…' : 'Create patient'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template picker */}
      <Dialog open={templatePickerOpen} onOpenChange={setTemplatePickerOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="crm-template-picker">
          <DialogHeader>
            <DialogTitle>Send WhatsApp template</DialogTitle>
            <DialogDescription>
              Pick one of your approved Meta templates. Fill any {'{{1}}'} placeholders below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {templates.length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center" data-testid="crm-no-templates">
                No approved templates found. Add one in Meta Business Manager.
              </p>
            )}
            {templates.map((t) => (
              <button
                key={t.id || t.name}
                onClick={() => { setTemplateSelected(t); setTemplateVars([]); }}
                className={`w-full text-left border rounded-lg px-3 py-2 hover:bg-emerald-50 transition ${templateSelected?.name === t.name ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}
                data-testid={`crm-template-${t.name}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                  <Badge className="bg-gray-100 text-gray-600 text-[10px]">{t.language}</Badge>
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {(t.components || []).find((c) => c.type === 'BODY')?.text || t.category}
                </p>
              </button>
            ))}

            {templateSelected && bodyVariableCount > 0 && (
              <div className="pt-2 border-t space-y-2">
                <p className="text-xs font-semibold text-gray-700">Template variables</p>
                {Array.from({ length: bodyVariableCount }).map((_, i) => (
                  <Input
                    key={i}
                    placeholder={`{{${i + 1}}}`}
                    value={templateVars[i] || ''}
                    onChange={(e) => {
                      const arr = [...templateVars];
                      arr[i] = e.target.value;
                      setTemplateVars(arr);
                    }}
                    data-testid={`crm-template-var-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplatePickerOpen(false)}>Cancel</Button>
            <Button
              onClick={sendTemplate}
              disabled={!templateSelected || sendingTemplate}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="crm-send-template-btn"
            >
              {sendingTemplate ? 'Sending…' : 'Send template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CRMPage;
